import { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  Star,
  User,
  Store,
  Calendar,
  MessageSquare,
  Flag,
  Trash2,
  Eye,
  ShieldCheck,
  RefreshCw,
  Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REPORT_REASONS = {
  'Contenu inapproprié': 'Contenu inapproprié',
  'Spam': 'Spam',
  'Faux avis': 'Faux avis',
  'Propos offensants': 'Propos offensants',
  'Autre': 'Autre',
  // Fallback for old format
  inappropriate: 'Contenu inapproprié',
  spam: 'Spam',
  fake: 'Faux avis',
  harassment: 'Propos offensants',
  other: 'Autre'
};

export default function AdminReviews() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchReportedReviews();
  }, []);

  const fetchReportedReviews = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/reviews/admin/reported`);
      setReports(response.data);
    } catch (error) {
      console.error('Error fetching reported reviews:', error);
      toast.error('Erreur lors du chargement des avis signalés');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (reportId, action) => {
    try {
      setProcessing(true);
      await axios.put(
        `${API_URL}/api/reviews/admin/reports/${reportId}?action=${action}`,
        {}
      );
      
      toast.success(action === 'remove' ? 'Avis supprimé' : 'Signalement rejeté');
      setShowConfirmModal(false);
      setSelectedReport(null);
      fetchReportedReviews();
    } catch (error) {
      console.error('Error handling report:', error);
      toast.error('Erreur lors du traitement du signalement');
    } finally {
      setProcessing(false);
    }
  };

  const openConfirmModal = (report, action) => {
    setSelectedReport(report);
    setConfirmAction(action);
    setShowConfirmModal(true);
  };

  const openDetailModal = (report) => {
    setSelectedReport(report);
    setShowDetailModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  return (
    <AdminLayout title="Modération des Avis">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-xl">
                <Flag className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">En attente</p>
                <p className="text-2xl font-bold">{reports.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 rounded-xl">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Priorité haute</p>
                <p className="text-2xl font-bold">
                  {reports.filter(r => r.reason === 'harassment' || r.reason === 'inappropriate').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-xl">
                <ShieldCheck className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Traités aujourd'hui</p>
                <p className="text-2xl font-bold">-</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Refresh Button */}
      <div className="flex justify-end mb-4">
        <Button
          variant="outline"
          onClick={fetchReportedReviews}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Reports List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Avis signalés
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
              <p className="text-gray-500">Aucun avis signalé en attente</p>
              <p className="text-sm text-gray-400 mt-1">Tous les signalements ont été traités</p>
            </div>
          ) : (
            <div className="space-y-4">
              {reports.map((report) => (
                <div
                  key={report.id}
                  className="border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors"
                >
                  <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                    {/* Review Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                          <Flag className="w-3 h-3 mr-1" />
                          {REPORT_REASONS[report.reason] || report.reason}
                        </Badge>
                        {report.review?.is_verified_purchase && (
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                            <ShieldCheck className="w-3 h-3 mr-1" />
                            Achat vérifié
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 mb-2">
                        {renderStars(report.review?.rating || 0)}
                        <span className="text-sm text-gray-500">
                          par {report.review?.client_name || 'Inconnu'}
                        </span>
                      </div>
                      
                      <p className="text-gray-700 mb-3 line-clamp-2">
                        "{report.review?.comment || 'Pas de commentaire'}"
                      </p>
                      
                      {report.review?.photos?.length > 0 && (
                        <div className="flex items-center gap-1 text-sm text-gray-500 mb-3">
                          <ImageIcon className="w-4 h-4" />
                          {report.review.photos.length} photo(s)
                        </div>
                      )}
                      
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Store className="w-4 h-4" />
                          {report.restaurant_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          Signalé par: {report.reporter_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(report.created_at)}
                        </span>
                      </div>
                      
                      {report.details && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Détails du signalement:</span> {report.details}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex lg:flex-col gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDetailModal(report)}
                        className="flex-1 lg:flex-none"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Détails
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openConfirmModal(report, 'dismiss')}
                        className="flex-1 lg:flex-none text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Rejeter
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openConfirmModal(report, 'remove')}
                        className="flex-1 lg:flex-none text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Supprimer
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Détails de l'avis signalé</DialogTitle>
          </DialogHeader>
          
          {selectedReport && (
            <div className="space-y-6">
              {/* Review Details */}
              <div className="border border-gray-200 rounded-xl p-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  L'avis
                </h4>
                <div className="flex items-center gap-2 mb-2">
                  {renderStars(selectedReport.review?.rating || 0)}
                  <span className="font-medium">{selectedReport.review?.rating}/5</span>
                </div>
                <p className="text-gray-700 mb-3">{selectedReport.review?.comment || 'Pas de commentaire'}</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Auteur:</span>
                    <span className="ml-2 font-medium">{selectedReport.review?.client_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Restaurant:</span>
                    <span className="ml-2 font-medium">{selectedReport.restaurant_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Date de l'avis:</span>
                    <span className="ml-2">{formatDate(selectedReport.review?.created_at)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Achat vérifié:</span>
                    <span className="ml-2">
                      {selectedReport.review?.is_verified_purchase ? (
                        <Badge className="bg-emerald-100 text-emerald-700">Oui</Badge>
                      ) : (
                        <Badge variant="secondary">Non</Badge>
                      )}
                    </span>
                  </div>
                </div>
                
                {selectedReport.review?.response && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-blue-700 mb-1">Réponse du restaurant:</p>
                    <p className="text-sm text-blue-600">{selectedReport.review.response}</p>
                  </div>
                )}
              </div>
              
              {/* Report Details */}
              <div className="border border-orange-200 rounded-xl p-4 bg-orange-50">
                <h4 className="font-semibold mb-3 flex items-center gap-2 text-orange-700">
                  <Flag className="w-4 h-4" />
                  Le signalement
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Raison:</span>
                    <span className="ml-2 font-medium">{REPORT_REASONS[selectedReport.reason]}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Signalé par:</span>
                    <span className="ml-2 font-medium">{selectedReport.reporter_name}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-600">Date:</span>
                    <span className="ml-2">{formatDate(selectedReport.created_at)}</span>
                  </div>
                  {selectedReport.details && (
                    <div className="col-span-2">
                      <span className="text-gray-600">Détails:</span>
                      <p className="mt-1 text-gray-700">{selectedReport.details}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Fermer
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowDetailModal(false);
                openConfirmModal(selectedReport, 'dismiss');
              }}
              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Rejeter le signalement
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowDetailModal(false);
                openConfirmModal(selectedReport, 'remove');
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer l'avis
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmAction === 'remove' ? 'Supprimer cet avis ?' : 'Rejeter ce signalement ?'}
            </DialogTitle>
            <DialogDescription>
              {confirmAction === 'remove' 
                ? "L'avis sera masqué et ne sera plus visible par les autres utilisateurs. Cette action est irréversible."
                : "Le signalement sera rejeté et l'avis restera visible. Le signaleur ne sera pas notifié."
              }
            </DialogDescription>
          </DialogHeader>
          
          {selectedReport && (
            <div className="my-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                {renderStars(selectedReport.review?.rating || 0)}
              </div>
              <p className="text-sm text-gray-700 line-clamp-2">
                "{selectedReport.review?.comment}"
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Par {selectedReport.review?.client_name} • {selectedReport.restaurant_name}
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowConfirmModal(false)}
              disabled={processing}
            >
              Annuler
            </Button>
            <Button
              variant={confirmAction === 'remove' ? 'destructive' : 'default'}
              onClick={() => handleAction(selectedReport?.id, confirmAction)}
              disabled={processing}
              className={confirmAction === 'dismiss' ? 'bg-emerald-500 hover:bg-emerald-600' : ''}
            >
              {processing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : confirmAction === 'remove' ? (
                <Trash2 className="w-4 h-4 mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              {confirmAction === 'remove' ? 'Supprimer' : 'Rejeter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
